<template>
  <div class="pdf-viewer-container">
    <div class="pdf-viewer-header">
      <h3 class="pdf-title">{{ title }}</h3>
      <div class="header-actions">
        <a v-if="pdfUrl" :href="pdfUrl" download class="download-btn">
          <i class="fa-solid fa-download"></i> Download PDF
        </a>
      </div>
    </div>
    <div class="pdf-content">
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p>Loading User Manual...</p>
      </div>
      <iframe v-else-if="pdfUrl" :src="pdfUrl" class="pdf-iframe"></iframe>
      <div v-else class="no-pdf">
        <i class="fa-solid fa-file-pdf"></i>
        <p>User Manual not found or failed to load.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const pdfUrl = ref('')
const title = ref('User Manual')
const loading = ref(false)

const loadPdf = () => {
  loading.value = true
  const url = route.query.url
  const lang = route.query.lang
  const queryTitle = route.query.title

  if (url) {
    pdfUrl.value = url
    title.value = queryTitle || 'User Manual'
  } else if (lang === 'bn') {
    pdfUrl.value = '/user-manual-bn.pdf'
    title.value = queryTitle || 'User Manual (Bangla)'
  } else {
    pdfUrl.value = '/user-manual-en.pdf'
    title.value = queryTitle || 'User Manual (English)'
  }
  
  // Simulate a small delay for smoother transition if needed, 
  // though iframe loading is handled by the browser
  setTimeout(() => {
    loading.value = false
  }, 300)
}

onMounted(() => {
  loadPdf()
})

// Watch for query parameter changes to reload the PDF
watch(() => route.query, () => {
  loadPdf()
}, { deep: true })
</script>

<style scoped>
.pdf-viewer-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.05);
  overflow: hidden;
  margin: 10px;
}

.pdf-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 25px;
  background: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
}

.pdf-title {
  margin: 0;
  font-size: 1.2rem;
  color: #4e73df;
  font-weight: 700;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.download-btn {
  background: #4e73df;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.download-btn:hover {
  background: #2e59d9;
  box-shadow: 0 4px 12px rgba(78, 115, 223, 0.3);
}

.pdf-content {
  flex: 1;
  background: #f1f1f1;
  position: relative;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #4e73df;
}

.loading-state p {
  margin-top: 15px;
  font-weight: 600;
}

.no-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #858796;
}

.no-pdf i {
  font-size: 4rem;
  margin-bottom: 15px;
}
</style>
